import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { CheckCircle2, ArrowRight, Loader2, XCircle } from "lucide-react";

/**
 * Payment success/confirmation page.
 * Polls /api/payments/status/{session_id} until payment_status === 'paid'
 * (or fails after N attempts). Then shows a confirmation card with CTA to enter
 * the league dashboard.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [record, setRecord] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    async function poll(n = 0) {
      if (cancelled || stopRef.current) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setRecord(data);
        setAttempts(n + 1);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setStatus(data.payment_status);
          return;
        }
        if (n >= 30) {
          // 30 polls * 2s ≈ 60s. Something might be off.
          setStatus("timeout");
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        if (n < 5) {
          setTimeout(() => poll(n + 1), 2000);
        } else {
          setStatus("failed");
        }
      }
    }
    poll(0);
    return () => {
      cancelled = true;
      stopRef.current = true;
    };
  }, [sessionId]);

  const leagueId = record?.league_id;

  return (
    <div className="min-h-screen bg-[#0b1120] text-white antialiased flex items-center justify-center px-5">
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1120] via-[#0d1428] to-[#0b1120]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-500/[0.1] rounded-full blur-[120px]" />
      </div>

      <div className="w-full max-w-md" data-testid="payment-success-card">
        {status === "polling" && (
          <div className="text-center">
            <Loader2 size={48} className="text-emerald-400 mx-auto animate-spin"/>
            <h1 className="mt-6 text-2xl font-black">Verifica in corso…</h1>
            <p className="mt-2 text-sm text-slate-400">
              Stiamo confermando il pagamento con Stripe.
              <br/>
              Attendi qualche secondo.
            </p>
            <div className="mt-4 text-xs text-slate-500 font-mono">
              Tentativo {attempts}/30
            </div>
          </div>
        )}

        {status === "paid" && (
          <div className="text-center rounded-3xl border border-emerald-500/40 bg-gradient-to-b from-emerald-500/[0.08] to-transparent p-8 shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500 shadow-[0_10px_30px_rgba(16,185,129,0.55)] mb-4">
              <CheckCircle2 size={40} className="text-[#0b1120]" strokeWidth={2.5}/>
            </div>
            <h1 className="text-3xl font-black" data-testid="payment-success-title">
              Pagamento completato!
            </h1>
            <p className="mt-3 text-sm text-slate-300">
              <span className="text-white font-bold">{record?.league_name}</span> è attiva per l&apos;intera stagione.
            </p>
            <div className="mt-5 flex items-center justify-center gap-6 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Importo</div>
                <div className="text-emerald-300 font-mono font-bold text-base mt-0.5">
                  {record?.amount?.toFixed(2)} €
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Ricevuta</div>
                <div className="text-slate-300 font-mono text-[10px] mt-0.5 max-w-[140px] truncate">
                  {sessionId?.slice(-16)}
                </div>
              </div>
            </div>

            <div className="mt-8 space-y-3">
              <button
                onClick={() => nav(`/lega/${leagueId}`)}
                disabled={!leagueId}
                data-testid="payment-success-goto-league"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#0b1120] font-black text-base transition-all shadow-[0_10px_28px_rgba(16,185,129,0.4)] hover:-translate-y-0.5 disabled:opacity-50"
              >
                Vai alla dashboard della lega <ArrowRight size={18}/>
              </button>
              <Link
                to="/dashboard"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-300 text-sm transition-colors"
              >
                Torna al menu principale
              </Link>
            </div>

            <div className="mt-6 text-[11px] text-slate-500">
              Riceverai una ricevuta via email da Stripe.
            </div>
          </div>
        )}

        {status === "timeout" && (
          <div className="text-center rounded-3xl border border-amber-500/40 bg-amber-500/5 p-8">
            <div className="text-amber-400 mb-4">
              <Loader2 size={48} className="mx-auto"/>
            </div>
            <h1 className="text-2xl font-black">Il pagamento sta ancora processando</h1>
            <p className="mt-3 text-sm text-slate-400">
              Stripe ci sta mettendo qualche istante più del solito. Ricarica la pagina fra un minuto,
              oppure controlla la tua email per la conferma.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"
            >
              Ricarica
            </button>
          </div>
        )}

        {["failed", "expired", "refunded"].includes(status) && (
          <div className="text-center rounded-3xl border border-rose-500/40 bg-rose-500/5 p-8">
            <XCircle size={48} className="text-rose-400 mx-auto mb-4"/>
            <h1 className="text-2xl font-black">Pagamento {status === "failed" ? "non riuscito" : status}</h1>
            <p className="mt-3 text-sm text-slate-400">
              Nessun addebito è stato effettuato. Puoi riprovare quando vuoi.
            </p>
            {leagueId && (
              <button
                onClick={() => nav(`/pagamento/${leagueId}`)}
                className="mt-6 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold"
              >
                Riprova il pagamento
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
